import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryData } from 'LoginUI/hooks/useQueryData';
import LocalStorage, { LOGIN_REQUIREMENT_KEY } from 'LoginUI/lib/LocalStorage';
import { getSignupUrl } from 'LoginUI/utils/urls';
import selectSessionInvalidationWarning from 'LoginUI/utils/selectSessionInvalidationWarning';
import { NO_RECORD_ERROR } from 'LoginUI/constants/ErrorValues';
import LoginDao from 'LoginUI/data/LoginDao';
// @ts-expect-error library not converted to TS
import Cookies from 'js-cookie';
// @ts-expect-error library not converted to TS
import HubSpotOnlyAppCookieLogin from 'Nabisco/app/HubSpotOnlyAppCookieLogin';
import LoginFailureHandlerFactory from 'LoginUI/lib/LoginFailureHandlerFactory';
import { useRedirect } from 'LoginUI/hooks/useRedirect';
import ConfirmToLoginAuthStatuses from 'LoginUI/constants/ConfirmToLoginAuthStatuses';
import { useHistory } from 'react-router-dom';
import { isValidEmail } from 'LoginUI/lib/LoginUtils';
import { DEFAULT_LOGIN_REQUIREMENTS } from 'LoginUI/constants/SmartLogin';
import { useBypass } from 'LoginUI/hooks/useBypass';
import { arePasskeysSupported } from 'LoginUI/utils/arePasskeysSupported';
import { PASSKEY } from 'LoginUI/constants/LoginSources';
const loginDao = new LoginDao();
const appCookieLogin = new HubSpotOnlyAppCookieLogin({
  loginRequest: loginDao.uiLogin.bind(loginDao),
  passThroughFallback: true
});
export const useLoginLandingPage = () => {
  // derived state
  const history = useHistory();
  const {
    allQueryParams,
    queryData
  } = useQueryData();
  const stagedLoginRequirement = useMemo(() => LocalStorage.getStagedLoginRequirement() || {}, []);
  const queryParamError = allQueryParams.loginError;
  const sessionInvalidationWarning = selectSessionInvalidationWarning(allQueryParams.authFailureReason);
  const signupUrl = getSignupUrl(queryData.signupParams, queryData.allowFbSignup, queryData.pageId, queryData.loginRedirectUrl);
  const stagedEmail = allQueryParams.email || stagedLoginRequirement.email;
  const stagedRememberLogin = useMemo(() => Cookies.get('prefs') === '1', []);
  const {
    bypass
  } = allQueryParams;
  // hook state
  const [error, setError] = useState(queryParamError || '');
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isEmailSet, setIsEmailSet] = useState(false);
  const [rememberLogin, setRememberLogin] = useState(stagedRememberLogin);
  const [isLoaded, setIsLoaded] = useState(false);
  const [availableLogins, setAvailableLogins] = useState([]);
  const [loginRequirementPortalId, setLoginRequirementPortalId] = useState();
  const [additionalSsoPortal, setAdditionalSsoPortal] = useState();
  const submittable = useMemo(() => {
    const emailSatisfied = !!email && isValidEmail(email);
    const passwordSatisfied = !!password && password.length >= 6;
    return !submitting && emailSatisfied && passwordSatisfied;
  }, [email, password, submitting]);

  // callbacks
  const {
    redirectByStatus,
    redirectToSuspiciousUser,
    redirectToLoginLink
  } = useRedirect(queryData);
  const {
    isBypassChecked
  } = useBypass({
    skipBypass: bypass === 'false',
    loginPortalId: queryData.loginPortalId,
    loginRedirectUrl: queryData.loginRedirectUrl
  });
  const toggleRememberLogin = useCallback(() => {
    setRememberLogin(!rememberLogin);
  }, [rememberLogin, setRememberLogin]);
  const setLoginTypes = loginTypes => {
    const passkeysSupported = arePasskeysSupported();
    if (loginTypes.length === 0) {
      // showing all login options if login requirements is empty array
      setAvailableLogins(DEFAULT_LOGIN_REQUIREMENTS);
    } else if (loginTypes.length === 1 && loginTypes[0] === PASSKEY && !passkeysSupported) {
      // showing all login options if the only login type is PASSKEY but passkeys aren't supported in the current browser
      setAvailableLogins(DEFAULT_LOGIN_REQUIREMENTS);
    } else {
      setAvailableLogins(loginTypes);
    }
  };
  const onClickNext = useCallback(() => {
    setSubmitting(true);
    loginDao.fetchLoginRequirement(email, queryData.loginPortalId).then(loginRequirementResponse => {
      setLoginTypes(loginRequirementResponse.loginTypes);
      setLoginRequirementPortalId(loginRequirementResponse.portalId);
      setAdditionalSsoPortal(loginRequirementResponse.ssoPortal);
      setIsEmailSet(true);
      setIsLoaded(true);
      setError('');
      LocalStorage.setJSON(LOGIN_REQUIREMENT_KEY, {
        email,
        rememberLogin
      });
    }).catch(err => {
      if (err.responseJSON && err.responseJSON.status === 'INVALID_USER') {
        setError(NO_RECORD_ERROR);
      } else {
        // showing all login options if failed to fetch login requirements
        setAvailableLogins(DEFAULT_LOGIN_REQUIREMENTS);
        setLoginRequirementPortalId(undefined);
        setIsLoaded(true);
        setError('');
        setIsEmailSet(true);
      }
    }).finally(() => {
      setSubmitting(false);
    });
  }, [email, rememberLogin, setError, setSubmitting, setIsEmailSet, queryData.loginPortalId]);
  const onPasswordSubmit = useCallback(() => {
    const {
      loginPortalId,
      loginRedirectUrl
    } = queryData;
    const portalId = loginRequirementPortalId || loginPortalId;
    const credentials = {
      email,
      password,
      rememberLogin,
      loginPortalId: portalId,
      loginRedirectUrl
    };
    appCookieLogin.login(credentials).then(responses => {
      const response = Object.assign({}, responses[0], {
        email,
        loginPortalId: portalId,
        loginRedirectUrl,
        password,
        rememberLogin
      });
      redirectByStatus(response, loginPortalId, rememberLogin);
    }).catch(errorResponse => {
      if (errorResponse.responseJSON && errorResponse.responseJSON.status === ConfirmToLoginAuthStatuses.SUSPICIOUS_USER_MUST_CONFIRM) {
        return redirectToSuspiciousUser({
          email,
          token: allQueryParams.token,
          password,
          rememberLogin,
          loginPortalId: portalId,
          loginRedirectUrl
        });
      } else if (errorResponse.responseJSON && errorResponse.responseJSON.status === ConfirmToLoginAuthStatuses.NEEDS_LOGIN_LINK) {
        return redirectToLoginLink();
      } else {
        return LoginFailureHandlerFactory.create(err => {
          setError(err);
          setSubmitting(false);
        }, history.push)(errorResponse);
      }
    });
  }, [loginRequirementPortalId, allQueryParams.token, email, history.push, password, queryData, redirectByStatus, redirectToSuspiciousUser, redirectToLoginLink, rememberLogin, setError, setSubmitting]);
  const onClickChangeEmail = useCallback(() => {
    setSubmitting(false);
    setAdditionalSsoPortal(undefined);
    setIsEmailSet(false);
    setIsLoaded(true);
    setEmail('');
    setError('');
    setPassword('');
    setAvailableLogins([]);
    setLoginRequirementPortalId(undefined);
  }, [setSubmitting, setIsEmailSet, setIsLoaded, setEmail, setError, setPassword, setAvailableLogins, setLoginRequirementPortalId]);

  // fetch login requirements immediately on load if we have an email
  useEffect(() => {
    if (isBypassChecked) {
      if (stagedEmail) {
        loginDao.fetchLoginRequirement(stagedEmail, queryData.loginPortalId).then(loginRequirementResponse => {
          setLoginTypes(loginRequirementResponse.loginTypes);
          setAdditionalSsoPortal(loginRequirementResponse.ssoPortal);
          setLoginRequirementPortalId(loginRequirementResponse.portalId);
          setIsEmailSet(true);
          setEmail(stagedEmail);
        }).catch(() => {
          setError('');
        }).finally(() => {
          setIsLoaded(true);
          setSubmitting(false);
        });
      } else {
        setIsLoaded(true);
      }
    }
  }, [stagedEmail, queryData.loginPortalId, isBypassChecked]);

  // hook return value
  return useMemo(() => ({
    email,
    error,
    availableLogins,
    additionalSsoPortal,
    isEmailSet,
    isFbSignin: queryData.allowFbSignup,
    isLoaded,
    loginPortalId: queryData.loginPortalId,
    loginRedirectUrl: queryData.loginRedirectUrl,
    loginRequirementPortalId,
    onClickNext,
    onPasswordSubmit,
    password,
    rememberLogin,
    sessionInvalidationWarning,
    setEmail,
    setIsEmailSet,
    setIsLoaded,
    setPassword,
    signupParams: queryData.signupParams,
    signupUrl,
    submittable,
    submitting,
    toggleRememberLogin,
    useDark: queryData.useDark,
    onClickChangeEmail,
    setError,
    pageId: queryData.pageId,
    isBypassChecked
  }), [email, error, availableLogins, additionalSsoPortal, isEmailSet, isLoaded, loginRequirementPortalId, onClickNext, onPasswordSubmit, password, queryData, rememberLogin, sessionInvalidationWarning, setEmail, setIsEmailSet, setIsLoaded, setPassword, signupUrl, submittable, submitting, toggleRememberLogin, onClickChangeEmail, setError, isBypassChecked]);
};